import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TasksToolbox } from '../components/tasks/tasksToolbox';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Project } from '../models/project';
import { AppMain } from '../layout/appMain';
import { Task } from '../components/tasks/task';
import { TaskDialogAdd } from '../components/tasks/taskDialogAdd';
import { Task as TaskModel } from '../models/task';
import { getProjectById } from '../services/api/projectService';
import TaskUsersDialog from '../components/tasks/taskUsersDialog';
import { deleteTask, getProjectTasks } from '../services/api/taskServices';
import { AppNoFeedback } from '../components/app/appNoFeedback';
import TaskDialogEdit from '../components/tasks/taskDialogEdit';
import { AppConfirmationDelete } from '../components/app/confirmation/appConfiramationDelete';
import { AppConfirmationSuccessful } from '../components/app/confirmation/appConfirmationSuccessful';
import useLoadItems from '../hooks/useLoadItems';
import AppIntersectionObserver from '../components/app/appIntersectionObserver';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useAppSnackbar from '../hooks/useAppSnackbar';
import AppLoading from '../components/app/appLoading';
import { useUserRoles } from '../hooks/useUserRoles';
import { useSelector } from 'react-redux';
import { selectProfileId } from '../store/profileSlice';
import TaskDialogDuplicate from '../components/tasks/taskDialogDuplicate';

interface RouterProp {
  project?: Project;
}

const style = {
  box: {
    mt: 4,
    display: 'flex',
    flexWrap: 'wrap',
  },
} as const;

export function PageTasks(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  const state: RouterProp = location.state || {};
  const URLparams = useParams();

  const [isOpenAdd, setIsOpenAdd] = useState<boolean>(false);
  const [taskProject, setTaskProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<TaskModel[]>([]);
  const [isOpenUsers, setIsOpenUsers] = useState<boolean>(false);
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);
  const [isOpenDuplicateDialog, setIsOpenDuplicateDialog] =
    useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<TaskModel | null>(null);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState<boolean>(false);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState<boolean>(false);
  const [isOpenUsersConfirmation, setIsOpenUsersConfirmation] =
    useState<boolean>(false);
  // take a look
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [query, setQuery] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [loadingTask, setLoadingTask] = useState<boolean>(true);

  const { onErrorSnackbar } = useAppSnackbar();

  const { result, isLoading, isFetching, hasMore } = useLoadItems({
    query,
    pageNumber,
    idProject: taskProject?.id,
    getMethod: getProjectTasks,
  });

  const { isAdmin } = useUserRoles();
  const profileId = useSelector(selectProfileId);

  const canEditAndDeleteProjectTasks = useMemo(
    () =>
      isAdmin ||
      (!!taskProject &&
        (taskProject.projectOwnerId === profileId ||
          taskProject.salesManagerId === profileId)),
    [isAdmin, profileId, taskProject]
  );

  const isLoadingActivities = useMemo(
    () => isLoading || isFetching,
    [isFetching, isLoading]
  );

  useEffect(() => {
    setTasks(result as TaskModel[]);
  }, [result]);

  const handleIntersect = useCallback(() => {
    if (isLoadingActivities) return;
    if (!hasMore) return;
    setPageNumber((prevPage) => prevPage + 1);
  }, [hasMore, isLoadingActivities]);

  useEffect(() => {
    if (state.project) {
      setTaskProject(state.project);
      setLoadingTask(false);

      return;
    }
    if (URLparams.id)
      getProjectById(URLparams.id)
        .then((response) => {
          setTaskProject(response);
        })
        .finally(() => {
          setLoadingTask(false);
        });
  }, [state.project, URLparams.id]);

  const switchPages = useCallback(() => {
    navigate('/projects');
  }, [navigate]);

  const onRequestSuccessfully = useCallback(() => {
    if (taskProject)
      getProjectTasks(taskProject.id)
        .then((response) => {
          setTasks(response.items);
        })
        .catch((err) => onErrorSnackbar(err));
  }, [onErrorSnackbar, taskProject]);

  const handleClose = useCallback(() => {
    setIsOpenAdd(false);
    setIsOpenUsers(false);
    setIsOpenEdit(false);
    setIsOpenDuplicateDialog(false);
    setIsOpenUsersConfirmation(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpenAdd(true);
  }, []);

  const handleOpenUsersDialog = useCallback(() => {
    setIsOpenUsers(true);
  }, []);

  const editTask = useCallback((task) => {
    setSelectedTask(task);
  }, []);

  const handleOpenEditDialog = useCallback(() => {
    setIsOpenEdit(true);
  }, []);

  const duplicateTask = useCallback((task) => {
    setSelectedTask(task);
    setIsOpenDuplicateDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsOpenDeleteDialog(false);
  }, []);

  const handleDeleteConfirmTask = useCallback(() => {
    if (selectedTask)
      deleteTask(selectedTask.id)
        .then(() => {
          handleCloseDeleteDialog();
          setIsOpenConfirmationDialog(true);
        })
        .then(() => {
          setTimeout(() => {
            handleCloseDeleteDialog();
            onRequestSuccessfully();
            setIsOpenConfirmationDialog(false);
          }, 500);
        })
        .catch((err) => onErrorSnackbar(err));
  }, [
    handleCloseDeleteDialog,
    onErrorSnackbar,
    onRequestSuccessfully,
    selectedTask,
  ]);

  const handleCloseConfirmationDialog = useCallback(() => {
    setIsOpenConfirmationDialog(false);
  }, []);

  const handleDeleteTask = useCallback((task) => {
    setIsOpenDeleteDialog(true);
    setSelectedTask(task);
  }, []);

  const handleUpdateTaskUsers = useCallback(() => {
    setIsOpenUsersConfirmation(true);
    onRequestSuccessfully();
  }, [onRequestSuccessfully]);

  const headerButtons = useMemo(() => {
    return [
      {
        icon: AddIcon,
        onClick: handleOpen,
      },
    ];
  }, [handleOpen]);

  return (
    <AppMain buttons={headerButtons}>
      <TasksToolbox
        currentProject={taskProject}
        onSwitch={switchPages}
        onOpen={handleOpen}
        loadingTask={loadingTask}
      />

      {!isLoadingActivities && tasks.length === 0 && (
        <AppNoFeedback info="Tasks" />
      )}

      {!isLoading && tasks.length !== 0 && (
        <Box sx={style.box}>
          {tasks.map((task) => (
            <Task
              key={task.id}
              openUsersDialog={handleOpenUsersDialog}
              task={task}
              canEdit={canEditAndDeleteProjectTasks}
              canDelete={canEditAndDeleteProjectTasks}
              onEdit={editTask}
              onDelete={handleDeleteTask}
              openEditDialog={handleOpenEditDialog}
              onDuplicate={duplicateTask}
            />
          ))}
        </Box>
      )}

      {isLoadingActivities && <AppLoading />}

      <AppIntersectionObserver intersect={handleIntersect} />

      <TaskDialogAdd
        isOpen={isOpenAdd}
        onClose={handleClose}
        onRequestSuccessfully={onRequestSuccessfully}
        project={taskProject}
      />

      <TaskDialogEdit
        isOpen={isOpenEdit}
        onClose={handleClose}
        task={selectedTask}
        onRequestSuccessfully={onRequestSuccessfully}
        project={taskProject}
      />

      <TaskDialogDuplicate
        isOpen={isOpenDuplicateDialog}
        onClose={handleClose}
        task={selectedTask}
        onRequestSuccessfully={onRequestSuccessfully}
      />

      {isOpenUsers ? (
        <TaskUsersDialog
          onClose={handleClose}
          task={selectedTask}
          handleUpdateTaskUsers={handleUpdateTaskUsers}
        />
      ) : null}

      <AppConfirmationDelete
        isOpen={isOpenDeleteDialog}
        message={`Delete Task ${selectedTask?.name}?`}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteConfirmTask}
      />
      <AppConfirmationSuccessful
        isOpen={isOpenConfirmationDialog}
        message="Task Deleted!"
        onClose={handleCloseConfirmationDialog}
      />

      <AppConfirmationSuccessful
        isOpen={isOpenUsersConfirmation}
        message="Updated successfully"
        onClose={handleCloseConfirmationDialog}
      />
    </AppMain>
  );
}
